import { PayinRail } from "@/modules/dashboard/payins/services/paymentsPayin.service";

/** The ways money can be collected in Colombia. All run on the payments runtime. */
export type ColombiaPayinMethod =
  | typeof PayinRail.BREB
  | typeof PayinRail.PSE
  | typeof PayinRail.NEQUI
  | typeof PayinRail.BANCOLOMBIA_BUTTON;

/**
 * Every Colombian rail the runtime can collect on. Pass it as `methods` from a surface that really builds a
 * payload for all four; one that builds fewer passes its own subset, which is what the required prop forces
 * it to state. Consumers are deliberately not enumerated here — the list would go stale the first time one is
 * added, which is the class of drift this whole file exists to prevent.
 *
 * Lives here rather than beside the component for two reasons: a file that exports components must not also
 * export constants (Fast Refresh), and `methods` is an effect dependency inside the picker, so callers need a
 * stable identity to pass — an inline array literal would be a new array on every render.
 */
export const ALL_COLOMBIA_PAYIN_METHODS: readonly ColombiaPayinMethod[] = [
  PayinRail.BREB,
  PayinRail.PSE,
  PayinRail.NEQUI,
  PayinRail.BANCOLOMBIA_BUTTON,
];

/**
 * The rails whose collection instrument is a bank page the payer is handed off to. They share PSE's whole
 * redirect lifecycle — `usePseRedirect` and the handoff screens — which is why the hook gates on the payin's
 * own rail rather than on a flag the caller passes.
 */
export const COLOMBIA_REDIRECT_METHODS: ReadonlySet<string> = new Set([
  PayinRail.PSE,
  PayinRail.BANCOLOMBIA_BUTTON,
]);

/**
 * Bre-B is the only Colombian rail whose instrument is a dynamic QR, so it is the only one that needs no
 * payer identity. Everything else registers the payer with the network up front.
 *
 * Stated as a negation of Bre-B rather than an enumeration on purpose: a new payer rail is then handled by
 * default, whereas `method === PSE || method === NEQUI || …` silently excludes it — which is precisely how
 * `/app/payins` came to create Nequi and Botón Bancolombia collections as Bre-B.
 */
export function colombiaMethodNeedsPayer(method: ColombiaPayinMethod): boolean {
  return method !== PayinRail.BREB;
}
